import { useEffect, useRef, useState } from 'react'

const MIN_CPS = 40
const MAX_CPS = 220
const CATCHUP_SECONDS = 0.4
const MAX_REVEAL_SECONDS = 2.5
const MIN_FRAME_SECONDS = 1 / 30

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)

export const useTypewriter = (content: string, streaming: boolean): string => {
  const reduceMotion = prefersReducedMotion()
  const [shown, setShown] = useState(() =>
    streaming && !reduceMotion ? 0 : content.length
  )
  const progressRef = useRef(shown)

  useEffect(() => {
    if (reduceMotion || progressRef.current >= content.length) {
      progressRef.current = Math.min(progressRef.current, content.length)

      return
    }

    let raf = 0
    let last = 0
    const step = (now: number) => {
      if (last === 0) last = now
      const dt = (now - last) / 1000
      if (dt < MIN_FRAME_SECONDS) {
        raf = requestAnimationFrame(step)

        return
      }
      last = now
      const target = content.length
      const prev = progressRef.current
      const gap = target - prev
      const cap = Math.max(MAX_CPS, gap / MAX_REVEAL_SECONDS)
      const cps = Math.min(Math.max(gap / CATCHUP_SECONDS, MIN_CPS), cap)
      const next = Math.min(target, prev + Math.ceil(cps * dt))
      progressRef.current = next
      setShown(next)
      if (next < target) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)

    return () => cancelAnimationFrame(raf)
  }, [content, reduceMotion])

  return reduceMotion ? content : content.slice(0, shown)
}
